import { useEffect, useState } from "react";

/**
 * Custom hook for managing search navigation through highlighted matches.
 * Works in conjunction with a highlighting implementation to navigate between matches.
 *
 * Features:
 * - Track current match index and total matches
 * - Navigate to next/previous match with Enter/Shift+Enter
 * - Scroll to current match
 * - Callback-based to work with any highlighting implementation
 *
 * @param searchQuery - The current search query
 * @param containerRef - Reference to the container element with highlighted matches
 * @param options - Configuration options
 * @returns Object containing navigation state and methods
 */
export function useSearchNavigation(
  searchQuery: string,
  containerRef: React.RefObject<HTMLElement>,
  options: {
    highlightClassName?: string;
    currentClassName?: string;
    enabled?: boolean;
    searchInputRef?: React.RefObject<HTMLInputElement>;
  } = {}
) {
  const {
    highlightClassName = "search-highlight",
    currentClassName = "search-highlight-current",
    enabled = true,
    searchInputRef,
  } = options;

  const [currentMatchIndex, setCurrentMatchIndex] = useState(-1);
  const [totalMatches, setTotalMatches] = useState(0);

  /**
   * Scroll to a match by scrolling the container, not the nested element.
   * This prevents the text layer from shifting relative to the canvas.
   */
  function scrollToMatch(
    element: Element,
    container: HTMLElement | null,
    behavior: ScrollBehavior = "smooth"
  ) {
    if (!container) return;

    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    /** Calculate the position of the element relative to the container's current scroll */
    const relativeTop = elementRect.top - containerRect.top + container.scrollTop;

    /** Center the element in the viewport */
    const scrollTo = relativeTop - containerRect.height / 2 + elementRect.height / 2;

    container.scrollTo({
      top: scrollTo,
      behavior: behavior,
    });
  }

  /** Update total matches when search query or highlights change */
  useEffect(() => {
    if (!enabled || !containerRef.current || !searchQuery.trim()) {
      setTotalMatches(0);
      setCurrentMatchIndex(-1);
      return;
    }

    /** Count highlights and scroll to first match */
    const updateMatches = () => {
      const marks = containerRef.current?.querySelectorAll(`.${highlightClassName}`);
      const count = marks?.length ?? 0;
      setTotalMatches(count);

      if (count > 0) {
        setCurrentMatchIndex(0);
        /** Highlight and scroll to first match */
        marks?.forEach(mark => mark.classList.remove(currentClassName));
        if (marks?.[0]) {
          marks[0].classList.add(currentClassName);
          scrollToMatch(marks[0], containerRef.current);
        }
      } else {
        setCurrentMatchIndex(-1);
      }
    };

    /** Delay to ensure highlights are rendered */
    const timeoutId = setTimeout(updateMatches, 100);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, enabled, containerRef, highlightClassName, currentClassName]);

  /** Navigate to next match */
  function navigateToNext() {
    if (totalMatches === 0 || !containerRef.current) return;

    const marks = containerRef.current.querySelectorAll(`.${highlightClassName}`);
    if (marks.length === 0) return;

    /** Remove current class from all marks */
    marks.forEach(mark => mark.classList.remove(currentClassName));

    /** Calculate next index (wrap around) */
    const nextIndex = (currentMatchIndex + 1) % totalMatches;
    setCurrentMatchIndex(nextIndex);

    /** Add current class and scroll to new match */
    const nextMark = marks[nextIndex];
    if (nextMark) {
      nextMark.classList.add(currentClassName);
      scrollToMatch(nextMark, containerRef.current, "smooth");
    }
  }

  /** Navigate to previous match */
  function navigateToPrevious() {
    if (totalMatches === 0 || !containerRef.current) return;

    const marks = containerRef.current.querySelectorAll(`.${highlightClassName}`);
    if (marks.length === 0) return;

    /** Remove current class from all marks */
    marks.forEach(mark => mark.classList.remove(currentClassName));

    /** Calculate previous index (wrap around) */
    const prevIndex = currentMatchIndex <= 0 ? totalMatches - 1 : currentMatchIndex - 1;
    setCurrentMatchIndex(prevIndex);

    /** Add current class and scroll to new match */
    const prevMark = marks[prevIndex];
    if (prevMark) {
      prevMark.classList.add(currentClassName);
      scrollToMatch(prevMark, containerRef.current, "smooth");
    }
  }

  /** Handle keyboard navigation (Enter for next, Shift+Enter for previous) */
  useEffect(() => {
    if (!enabled) return;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Enter" && totalMatches > 0) {
        e.preventDefault();
        if (e.shiftKey) {
          navigateToPrevious();
        } else {
          navigateToNext();
        }
      }
    }

    /** Listen on search input if provided */
    if (searchInputRef?.current) {
      searchInputRef.current.addEventListener("keydown", handleKeyDown);
    }

    /** Also listen on container for clicks/focus inside PDF */
    if (containerRef.current) {
      containerRef.current.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      if (searchInputRef?.current) {
        searchInputRef.current.removeEventListener("keydown", handleKeyDown);
      }
      if (containerRef.current) {
        containerRef.current.removeEventListener("keydown", handleKeyDown);
      }
    };
  }, [enabled, totalMatches, currentMatchIndex, searchInputRef, containerRef]);

  return {
    currentMatchIndex,
    totalMatches,
    navigateToNext,
    navigateToPrevious,
    hasMatches: totalMatches > 0,
  };
}
